/**
 * Dates that know how much they know.
 *
 * The whole point is that "2027" is not 2027-01-01. Treating it as one makes
 * the differ hallucinate a change the moment a real date arrives, and makes
 * the app claim a book is out on New Year's Day.
 */

import type { Precision, SlateDate } from './types.js';

const FULL = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH = /^(\d{4})-(\d{2})$/;
const YEAR = /^(\d{4})$/;

/**
 * Parse whatever a source gave us. Accepts 'YYYY-MM-DD', 'YYYY-MM', 'YYYY',
 * and tolerates the zero-filled forms some catalogues emit for partial dates
 * ('2027-00-00', '2027-09-00'), which are a coarse date wearing a full date's
 * clothes — exactly the case that must not become 1 January.
 */
export function parseDate(raw: string | null | undefined): SlateDate | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;

  const full = FULL.exec(s);
  if (full) {
    const [, y, m, d] = full as unknown as [string, string, string, string];
    if (m === '00' && d === '00') return { value: y, precision: 'year' };
    if (d === '00') return { value: `${y}-${m}`, precision: 'month' };
    if (!isRealDate(+y, +m, +d)) return null;
    return { value: s, precision: 'day' };
  }

  const month = MONTH.exec(s);
  if (month) {
    const [, y, m] = month as unknown as [string, string, string];
    if (+m < 1 || +m > 12) return null;
    return { value: `${y}-${m}`, precision: 'month' };
  }

  const year = YEAR.exec(s);
  if (year) return { value: s, precision: 'year' };

  return null;
}

function isRealDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export function sameDate(a: SlateDate | null, b: SlateDate | null): boolean {
  if (a === null || b === null) return a === b;
  return a.value === b.value && a.precision === b.precision;
}

const RANK: Record<Precision, number> = { year: 0, month: 1, day: 2 };

/**
 * True when `next` says the same thing as `prev` but more exactly — '2027'
 * becoming '2027-03-12'. That is news ("we now know the day") but it is not
 * a delay, and calling it one would cry wolf on every schedule firming up.
 */
export function isRefinement(prev: SlateDate, next: SlateDate): boolean {
  if (RANK[next.precision] <= RANK[prev.precision]) return false;
  return next.value.startsWith(prev.value);
}

/** Inclusive last instant covered by a date, honouring its precision. */
export function rangeEnd(d: SlateDate): Date {
  const [y, m] = d.value.split('-');
  const year = Number(y);
  switch (d.precision) {
    case 'year':
      return new Date(Date.UTC(year + 1, 0, 1) - 1);
    case 'month':
      return new Date(Date.UTC(year, Number(m), 1) - 1);
    case 'day':
      return new Date(`${d.value}T23:59:59.999Z`);
  }
}

/** First instant covered by a date. Used for ordering, not for release. */
export function rangeStart(d: SlateDate): Date {
  switch (d.precision) {
    case 'year':
      return new Date(`${d.value}-01-01T00:00:00Z`);
    case 'month':
      return new Date(`${d.value}-01T00:00:00Z`);
    case 'day':
      return new Date(`${d.value}T00:00:00Z`);
  }
}

/**
 * Has this actually come out?
 *
 * Deliberately uses the END of the range. If all we know is "2027" then on
 * 2 January 2027 we do not know it is out — claiming otherwise would put a
 * book in your "ready to read" pile eleven months early, which is the single
 * most annoying thing this app could do.
 */
export function hasPassed(d: SlateDate, now: Date): boolean {
  return rangeEnd(d).getTime() < now.getTime();
}

/** Chronological sort key. Coarse dates sort to the start of their range. */
export function compareDates(a: SlateDate, b: SlateDate): number {
  const diff = rangeStart(a).getTime() - rangeStart(b).getTime();
  if (diff !== 0) return diff;
  return RANK[a.precision] - RANK[b.precision];
}

/** Whole days from `now` until a date opens. Negative once it has started. */
export function daysUntil(d: SlateDate, now: Date): number {
  const ms = rangeStart(d).getTime() - now.getTime();
  return Math.floor(ms / 86_400_000);
}
